/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX, MouseEvent } from "react";

import caretIcon from "../../../model/assets/images/caret.svg";
import type { CaretProps } from "./CaretProps";
import "./Caret.scss";

const DIRECTION_LABEL: Record<CaretProps["direction"], string> = {
  up: "Scroll up",
  down: "Scroll down",
  right: "Scroll right",
  left: "Scroll left"
};

/**
 * Generic 4-direction spatial-navigation scroll indicator, opt-in for any
 * screen with a scrollable list/grid. A native <button> — free keyboard
 * focus/activation and disabled handling, no manual role/tabIndex/onKeyDown
 * needed. Direction is expressed purely via a CSS modifier class (see
 * Caret.scss) so this component owns no screen-specific positioning —
 * callers place it via `className`.
 */
const Caret: FunctionComponent<CaretProps> = (
  props: CaretProps
): JSX.Element => {
  const {
    direction,
    isDisabled,
    onClick,
    className,
    onMouseEnter,
    onMouseLeave,
    hideFromScreenReader = false
  } = props;

  const modifier: string = isDisabled ? "caret--inactive" : "caret--active";
  const directionModifier = `caret--${direction}`;

  const classNames = ["caret", modifier, directionModifier, className]
    .filter(Boolean)
    .join(" ");

  // Consumers (Guide, carousels, ...) drive their own scroll/highlight state
  // on click — nothing in those D-pad/spatial-nav models ever moves real
  // DOM focus elsewhere afterward. Left alone, this native <button> keeps
  // genuine browser focus indefinitely after a pointer click, so its
  // hover/focus-visible glow (Caret.scss) stays visibly "stuck" on until
  // some other native-focusable element happens to be clicked. Blurring
  // right after the click clears that state immediately.
  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    onClick();
    event.currentTarget.blur();
  };

  return (
    <button
      type="button"
      className={classNames}
      aria-label={DIRECTION_LABEL[direction]}
      aria-hidden={hideFromScreenReader ? true : undefined}
      tabIndex={hideFromScreenReader ? -1 : undefined}
      disabled={isDisabled}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <img
        className="caret__chevron"
        src={caretIcon}
        alt=""
        aria-hidden="true"
        draggable={false}
      />
    </button>
  );
};

export default Caret;
